import { useSortable } from "@dnd-kit/sortable";
import React, { useEffect, useState } from "react";
import { CSS } from "@dnd-kit/utilities";
import { SidebarMenuItemType } from "../../../services/helpers/types";
import Button from "../Button/Button";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import classNames from "classnames";
import { CopyAll, DeleteOutline } from "@mui/icons-material";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { scrollToElement } from "../../../services/helpers/commonHelpers";
import { duplicateSidebarItem } from "../../../services/api/sidebar.api";
import { useDispatch } from "react-redux";
import {
  setIsFieldsLoading,
  setIsReportLoading,
  setIsSidebarLoading,
  setIsTabsLoading,
} from "../../../redux/reducers/loadingReducer";
import { useTranslation } from "react-i18next";

interface DraggableSidebarItemProps {
  id: number;
  sidebarItem: SidebarMenuItemType | undefined;
  currentSidebarItem: SidebarMenuItemType | undefined;
  onPageChange: (itemID: number, shouldRefresh?: boolean) => void;
  onDelete: (itemID: number) => void;
}

const DraggableSidebarItem = (props: DraggableSidebarItemProps) => {
  const { id, sidebarItem, currentSidebarItem, onPageChange, onDelete } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isCopyOpen, setIsCopyOpen] = useState(false);

  const isActive =
    currentSidebarItem?.sidebarItemID === sidebarItem?.sidebarItemID;

  useEffect(() => {
    if (isActive) {
      scrollToElement("dui-sidebar-mid", "dui-sidebar-item-active");
    }
  }, [isActive]);

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const className = classNames("dui-sidebar-edit-item", {
    "dui-sidebar-edit-item-active": isActive,
  });

  const handlePageChange = (itemID?: number) => {
    if (itemID !== undefined) {
      onPageChange(itemID);
    }
  };

  const handleConfirmDelete = () => {
    if (sidebarItem?.sidebarItemID !== undefined) {
      onDelete(sidebarItem?.sidebarItemID);
    }
  };

  const handleCopyItem = () => {
    dispatch(setIsSidebarLoading(true));
    dispatch(setIsTabsLoading(true));
    dispatch(setIsFieldsLoading(true));
    dispatch(setIsReportLoading(true));
    if (sidebarItem?.sidebarItemID !== undefined) {
      const copyName = t("copyName");
      duplicateSidebarItem(dispatch, sidebarItem?.sidebarItemID, copyName).then(
        (result) => {
          onPageChange(result.sidebarItemID, true);
        },
      );
    }
    setIsCopyOpen(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={className}
      id={isActive ? "dui-sidebar-item-active" : ""}
    >
      <div className="dui-sidebar-edit-item-buttons">
        <div {...attributes} {...listeners} className="dui-btn-inner">
          <Button icon={<UnfoldMoreIcon />} type="text" />
        </div>
        <div className="dui-btn-inner">
          <Button
            icon={<DeleteOutline />}
            type="text"
            onClick={() => {
              setIsDeleteOpen(true);
            }}
          />
        </div>
      </div>
      <div
        className="dui-sidebar-edit-item-name"
        onClick={() => handlePageChange(sidebarItem?.sidebarItemID)}
      >
        {sidebarItem?.name}
      </div>
      <div className="dui-sidebar-copy-button">
        <Button
          icon={<CopyAll />}
          type="text"
          onClick={() => setIsCopyOpen(true)}
        ></Button>
      </div>
      <ConfirmDialog
        open={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t("deleteTitle")}
        description={`${t("areYouSureDelete1")} ${sidebarItem?.name} ${t("areYouSureDelete2")}`}
        confirmText={t("delete")}
        cancelText={t("cancel")}
        confirmButtonAppearance="error"
      />
      <ConfirmDialog
        open={isCopyOpen}
        onClose={() => setIsCopyOpen(false)}
        onConfirm={handleCopyItem}
        title={t("duplicateTitle")}
        description={`${t("areYouSureDuplicate")} ${sidebarItem?.name}?`}
        confirmText={t("duplicate")}
        cancelText={t("cancel")}
      />
    </div>
  );
};

export default DraggableSidebarItem;
